/**
 * The fast lane's wire contract (the Jev reflex layer, spec
 * docs/superpowers/specs/2026-09-19-jev-reflex-layer-design.md, plan
 * docs/superpowers/plans/2026-09-19-jev-rail-and-tier-s.md).
 *
 * Declared once here so the box (the caller) and the server (the brain) can never
 * disagree on a key. The box sends what it SEES (a snapshot and its interactive
 * elements) and the NAMES of the values it may type; the server answers with one
 * step or a hand-back. Input VALUES never cross this wire: the box types them
 * itself, and a secure field is typed by the existing masked path.
 */
import { z } from 'zod';

// BACK sits with the scrolls because it is the same KIND of step: it moves the view
// rather than activating a control, so it has no target to pick and no risk read to
// pass. Without it a stretch could only ever go forwards: the lane opened a search
// result, found it was the wrong one, and had no way back to the list.
//
// WHAT IT DOES NOT DO, so nobody tunes toward it: it does not turn "open the first
// three results and read each" into one stretch. The planner is handed only the
// FINAL page's snapshot, so a single stretch over three results would leave it
// holding result three and nothing of the other two. That is three goals. BACK is
// for a stretch whose goal is met on the page it ends on — "open it, it is the
// wrong one, go back, open the next".
export const LANE_OPERATIONS = [
  'CLICK', 'TYPE', 'SELECT', 'PRESS_ENTER', 'SCROLL_DOWN', 'SCROLL_UP', 'BACK', 'WAIT', 'DONE', 'BLOCKED',
] as const;
export type LaneOperation = (typeof LANE_OPERATIONS)[number];

/** Every way a stretch can stop short of done. Each one is a normal tool result,
 *  never an error: the planner carries on exactly as it does today. */
export const LANE_HANDBACK_REASONS = [
  'unsure', 'stuck', 'blocked', 'risky_step', 'step_budget', 'dialog_open',
  'user_has_wheel', 'origin_blocked', 'stale_page', 'jev_unavailable', 'aborted', 'tool_refused',
] as const;
export type LaneHandbackReason = (typeof LANE_HANDBACK_REASONS)[number];

/**
 * WHICH RULE GAVE WAY on an `unsure` hand-back. `unsure` is one reason covering
 * eight rules, so "it could not tell what the next step should be" is all the
 * planner ever learns — and on a results page that is the difference between
 * "there is nothing to click here" and "there are thirty things and two of them
 * are tied". Keys, never prose: each one names a rule in lane-verdict.ts.
 */
export const LANE_UNSURE_WHY = [
  /** Rule 4: not sure enough WHAT to do at all (or no readable operation). */
  'operation_low',
  /** Rule 6/6b/7: nothing was above the target floor. */
  'no_clear_target',
  /** Rule 6/6b/7: two targets a hair apart — a tie, not a 51% answer. */
  'near_tie',
  /** Rule 6b/7: the field was clear but the value or option was not. */
  'value_low',
  /** An answer we never offered: an invented operation, option or input name. */
  'not_offered',
] as const;
export type LaneUnsureWhy = (typeof LANE_UNSURE_WHY)[number];

export const LaneCandidateSchema = z.object({
  ref: z.string().min(2).max(16),
  role: z.string().max(64).optional(),
  name: z.string().max(256).optional(),
  inView: z.boolean().optional(),
  typeable: z.boolean().optional(),
  // What the page says about the control's STATE — whether a box is ticked, a field
  // already has something in it, a menu is open. Never what it CONTAINS: a filled field
  // says `filled: true` and nothing else, so the value stays on the box. Every one is
  // optional because a lane that cannot read state (the Mac lane) simply sends none.
  filled: z.boolean().optional(),
  checked: z.union([z.boolean(), z.literal('mixed')]).optional(),
  selected: z.boolean().optional(),
  expanded: z.boolean().optional(),
  disabled: z.boolean().optional(),
  /** A native dropdown's option LABELS, which are page content rather than anything the
   *  user typed. Present only for a `<select>`; the lane picks one BY LABEL, never by an
   *  index the vendor could invent. */
  options: z.array(z.string().max(256)).max(50).optional(),
});
export type LaneCandidate = z.infer<typeof LaneCandidateSchema>;

export const LaneStepRequestSchema = z.object({
  lane: z.enum(['browser', 'os']),
  taskId: z.string().min(1).max(128),
  goal: z.string().min(1).max(2000),
  /** Names only. The values stay on the box. */
  inputNames: z.array(z.string().min(1).max(64)).max(32),
  url: z.string().max(2048).optional(),
  app: z.string().max(256).optional(),
  title: z.string().max(512).optional(),
  history: z.array(z.string().max(300)).max(24),
  snapshotText: z.string().max(120_000),
  candidates: z.array(LaneCandidateSchema).min(1).max(600),
  /** Whether the box's hands gate is on: with it on, a careful origin asks a human
   *  for every click, so the lane has no business there. */
  gateOn: z.boolean(),
});
export type LaneStepRequest = z.infer<typeof LaneStepRequestSchema>;

export const LaneStepResponseSchema = z.object({
  verdict: z.enum(['act', 'handback', 'done', 'unavailable']),
  reason: z.enum(LANE_HANDBACK_REASONS).optional(),
  step: z
    .object({
      operation: z.enum(LANE_OPERATIONS),
      ref: z.string().optional(),
      inputName: z.string().optional(),
      /** On SELECT: the option's visible LABEL, always one the candidate offered — so it
       *  carries the same bound as the labels it is picked from. */
      option: z.string().max(256).optional(),
      confidence: z.number(),
    })
    .optional(),
  goalMet: z.number(),
  stuck: z.number(),
  /** On a risky_step hand-back: the control the planner should handle itself. */
  risky: z.object({ ref: z.string(), label: z.string().optional() }).optional(),
  /** On an `unsure` hand-back: which rule gave way. */
  why: z.enum(LANE_UNSURE_WHY).optional(),
  /**
   * The two the lane could not choose between, best first — on `near_tie` and
   * `no_clear_target` only, where naming them turns a dead end into one click
   * the planner can make. REFS AND PROBABILITIES, which are not values: the box
   * resolves each ref back to the control's own label off its last look, so no
   * page text has to come back over this wire to say it.
   */
  contenders: z.array(z.object({ ref: z.string(), p: z.number() })).max(2).optional(),
});
export type LaneStepResponse = z.infer<typeof LaneStepResponseSchema>;

/**
 * THE STRETCH'S ONLY DURABLE TRACE, and the lane's narration on its way to the
 * task's story (EVENTS.LANE_STORY, minted into a `task_story` row by the server).
 *
 * Both drills of the founder's Amazon prompt were unreadable afterwards for the
 * same reason: a stretch's whole record was one box log line, and the box's logs
 * had rotated twice by the time anyone read the task. `why` — which of the eight
 * `unsure` rules gave way — was simply gone. A row survives a rotation, a
 * redeploy and a restart.
 *
 * TWO SHAPES, ONE EVENT:
 *   `step` the driver's own narration for one act, exactly the line the user
 *          already sees live on the progress rail. Label-only by construction
 *          (browser-driver.ts `narrate` names the FIELD, never the value).
 *   `lane`  ONE per stretch, on done and on every hand-back: numbers, closed-list
 *          keys and the box's own opaque refs (lane-core.ts `laneStretchRecord`).
 *          Never a goal, a label, a value, a url or snapshot text.
 *
 * `seq` is the entry's place within this stretch, and `toolUseId` the tool call it
 * belongs to; together they make the row's idempotency key, so a frame delivered
 * twice is one row.
 */
export const LaneStoryPayloadSchema = z.object({
  taskId: z.string().min(1).max(128),
  toolUseId: z.string().min(1).max(128),
  lane: z.enum(['browser', 'os']),
  kind: z.enum(['step', 'lane']),
  line: z.string().min(1).max(2000),
  seq: z.number().int().min(0),
});
export type LaneStoryPayload = z.infer<typeof LaneStoryPayloadSchema>;

export const LaneFindRequestSchema = z.object({
  lane: z.enum(['browser', 'os']),
  taskId: z.string().min(1).max(128),
  query: z.string().min(1).max(200),
  title: z.string().max(512).optional(),
  candidates: z.array(LaneCandidateSchema).min(1).max(2000),
});
export type LaneFindRequest = z.infer<typeof LaneFindRequestSchema>;

export const LaneFindResponseSchema = z.object({
  verdict: z.enum(['ok', 'unavailable']),
  hits: z.array(z.object({ ref: z.string(), p: z.number() })).max(5),
});
export type LaneFindResponse = z.infer<typeof LaneFindResponseSchema>;
